import React from 'react';
import {
  NavigationMenu as NavMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuLink,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { NavigationLink } from './NavigationLink';

export interface NavItem {
  key: string;
  label: string;
  href?: string;
}

export interface NavigationMenuProps {
  items: NavItem[];
  activeKey?: string;
  onItemClick?: (key: string) => void;
  onItemHover?: (key: string | null) => void;
  className?: string;
  /** Custom link component for routing (e.g., React Router Link) */
  linkComponent?: React.ElementType;
}

export const NavigationMenu: React.FC<NavigationMenuProps> = ({
  items,
  activeKey,
  onItemClick,
  onItemHover,
  className,
  linkComponent
}) => {
  return (
    <NavMenu className={cn("hidden md:block", className)}>
      <NavigationMenuList className="gap-2">
        {items.map((item) => (
          <NavigationMenuItem 
            key={item.key}
            onMouseEnter={() => onItemHover?.(item.key)}
            onMouseLeave={() => onItemHover?.(null)}
          >
            <NavigationMenuLink asChild>
              <NavigationLink
                as={linkComponent}
                href={item.href}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  if (!linkComponent) {
                    e.preventDefault();
                  }
                  onItemClick?.(item.key);
                }}
                className={cn(
                  navigationMenuTriggerStyle(),
                  "relative transition-all duration-200",
                  // Tablet styles
                  "md:h-10 md:rounded-3xl md:bg-sky-700/20 md:border md:border-sky-600/30",
                  "md:hover:bg-sky-700/30 md:hover:border-sky-600/40",
                  // Desktop styles  
                  "lg:h-8 lg:rounded-full lg:bg-transparent lg:border-0",
                  "lg:hover:bg-white/10 lg:hover:ring-1 lg:hover:ring-white/15",
                  // Active state
                  activeKey === item.key && [
                    "md:bg-sky-700/30 md:border-sky-600/40",
                    "lg:bg-white/15 lg:ring-1 lg:ring-white/20"
                  ]
                )}
                data-active={activeKey === item.key || undefined}
                aria-current={activeKey === item.key ? 'page' : undefined}
              >
                <span>{item.label}</span>
                <ChevronDown className="hidden md:inline-block lg:inline-block h-3 w-3 ml-1 opacity-80" />
              </NavigationLink>
            </NavigationMenuLink>
          </NavigationMenuItem>
        ))}
      </NavigationMenuList>
    </NavMenu>
  );
};

NavigationMenu.displayName = 'NavigationMenu';
